/**
 * 🧪 E2E 테스트용 data-testid 상수 관리
 * 
 * 모든 테스트 ID를 중앙에서 관리하여 일관성 유지
 * Page별로 그룹화하여 체계적으로 관리
 */

// ===== LandingPage Test IDs =====
export const LANDING_PAGE_TEST_IDS = {
  // Hero Section
  HERO_SECTION: 'hero-section',
  HERO_TITLE: 'hero-title',
  HERO_DESCRIPTION: 'hero-description',
  
  // Buttons
  CTA_BUTTON: 'cta-button',
  LOGIN_BUTTON: 'login-button',
  START_LEARNING_BUTTON: 'start-learning-button',
  
  // Features
  FEATURES_SECTION: 'features-section',
  FEATURE_CARD: 'feature-card',
  
  // Search
  SEARCH_INPUT: 'search-input',
  SEARCH_BUTTON: 'search-button',
} as const;

// ===== AuthServer Test IDs =====
export const AUTH_SERVER_TEST_IDS = {
  // Login Forms
  LOGIN_FORM: 'authserver-login-form',
  MOBILE_LOGIN_FORM: 'authserver-mobile-login-form',
  
  // Signup Forms  
  SIGNUP_FORM: 'authserver-signup-form',
  SIGNUP_USERNAME_INPUT: 'authserver-signup-username-input',
  SIGNUP_EMAIL_INPUT: 'authserver-signup-email-input',
  SIGNUP_PASSWORD_INPUT: 'authserver-signup-password-input',
  SIGNUP_CONFIRM_PASSWORD_INPUT: 'authserver-signup-confirm-password-input',
  SIGNUP_TERMS_CHECKBOX: 'authserver-signup-terms-checkbox',
  SIGNUP_BUTTON: 'authserver-signup-button',
  
  // Input Fields
  USERNAME_INPUT: 'authserver-username-input',
  PASSWORD_INPUT: 'authserver-password-input',
  EMAIL_INPUT: 'authserver-email-input',
  
  // Buttons
  LOGIN_BUTTON: 'authserver-login-button',
  SUBMIT_BUTTON: 'authserver-submit-button',
  GOOGLE_OAUTH_BUTTON: 'google-oauth-button',

  // OAuth consent
  CONSENT_TITLE: 'consent-title',
  CONSENT_CLIENT_NAME: 'consent-client-name',
  CONSENT_USER_NAME: 'consent-user-name',
  CONSENT_SCOPE_TITLE: 'consent-scope-title',
  CONSENT_REQUIRED_NOTICE: 'consent-required-notice',
  CONSENT_APPROVE_BUTTON: 'consent-approve-button',
  CONSENT_DENY_BUTTON: 'consent-deny-button',
  CONSENT_LOADING: 'consent-loading',
  CONSENT_ERROR: 'consent-error',
  
  // Messages
  ERROR_MESSAGE: 'error-message',
  SUCCESS_MESSAGE: 'success-message',
} as const;

// ===== MainLayout Test IDs =====
export const MAIN_LAYOUT_TEST_IDS = {
  // Sidebar
  SIDEBAR: 'sidebar',
  SIDEBAR_TOGGLE: 'sidebar-toggle',
  SIDEBAR_MENU: 'sidebar-menu',
  SIDEBAR_ITEM: 'sidebar-item',
  
  // Header
  HEADER: 'header',
  HEADER_LOGO: 'header-logo',
  HEADER_SEARCH: 'header-search',
  HEADER_USER_MENU: 'header-user-menu',
  
  // User Profile
  USER_PROFILE: 'user-profile',
  USER_AVATAR: 'user-avatar',
  USER_NAME: 'user-name',
  USER_DROPDOWN: 'user-dropdown',
  
  // Navigation
  NAV_FEED: 'nav-feed',
  NAV_DISCOVER: 'nav-discover',
  NAV_LEARN: 'nav-learn',
  NAV_PROFILE: 'nav-profile',
  NAV_SETTINGS: 'nav-settings',
} as const;

// ===== Feed Page Test IDs =====
export const FEED_PAGE_TEST_IDS = {
  // Post List
  POST_LIST: 'post-list',
  POST_ITEM: 'post-item',
  POST_TITLE: 'post-title',
  POST_CONTENT: 'post-content',
  
  // Create Post
  CREATE_POST_BUTTON: 'create-post-button',
  POST_EDITOR: 'post-editor',
  POST_SUBMIT: 'post-submit',
  
  // Interactions
  LIKE_BUTTON: 'like-button',
  COMMENT_BUTTON: 'comment-button',
  SHARE_BUTTON: 'share-button',
} as const;

// ===== Tweet Composer Test IDs =====
export const TWEET_COMPOSER_TEST_IDS = {
  COMPOSE_TRIGGER: 'compose-tweet-button',
  COMPOSER_CONTAINER: 'tweet-composer',
  TEXTAREA: 'tweet-text-area',
  CHARACTER_COUNT: 'character-count',
  MEDIA_BUTTON: 'attach-image-button',
  SUBMIT_BUTTON: 'submit-tweet-button',
  FILE_INPUT: 'tweet-media-input',
  MEDIA_PREVIEW: 'tweet-media-preview',
  MODAL_CONTAINER: 'compose-modal',
  REMOVE_MEDIA_BUTTON: 'tweet-media-remove-button',
} as const;

// ===== Settings Page Test IDs =====
export const SETTINGS_PAGE_TEST_IDS = {
  // Tabs
  SETTINGS_TAB_GENERAL: 'settings-tab-general',
  SETTINGS_TAB_PRIVACY: 'settings-tab-privacy',
  SETTINGS_TAB_NOTIFICATIONS: 'settings-tab-notifications',
  SETTINGS_TAB_DEBUG: 'settings-tab-debug',
  
  // Language Settings
  LANGUAGE_SELECT: 'language-select',
  THEME_SELECT: 'theme-select',
  
  // Debug Settings
  DEBUG_FETCH_EMAIL: 'debug-fetch-email',
  DEBUG_SHOW_ID_TOKEN: 'debug-show-id-token',
  
  // Actions
  SAVE_SETTINGS: 'save-settings',
  CANCEL_SETTINGS: 'cancel-settings',
} as const;

// ===== Course Edit Page Test IDs =====
export const COURSE_EDIT_TEST_IDS = {
  LANGUAGE_SECTION: 'course-edit-language-section',
  LANGUAGE_EDIT_SELECT: 'course-edit-language-edit-select',
  LANGUAGE_SUPPORTED_SELECT: 'course-edit-language-supported-select',
  LANGUAGE_DEFAULT_SELECT: 'course-edit-language-default-select',
  LANGUAGE_EDIT_OPTION_PREFIX: 'course-edit-language-edit-option',
  LANGUAGE_SUPPORTED_OPTION_PREFIX: 'course-edit-language-supported-option',
  LANGUAGE_DEFAULT_OPTION_PREFIX: 'course-edit-language-default-option',
  LANGUAGE_TITLE_INPUT: 'course-edit-language-title-input',
  LANGUAGE_LEVEL_INPUT: 'course-edit-language-level-input',
  LANGUAGE_COPY_GROUP: 'course-edit-language-copy-group',
  LANGUAGE_COPY_FROM_DEFAULT_BUTTON: 'course-edit-language-copy-button',
  LANGUAGE_CHIPS: 'course-edit-language-chips',
  LANGUAGE_STATE_DEBUG: 'course-edit-language-state-debug',
  CURRICULUM_TOOLBAR: 'course-edit-curriculum-language-toolbar',
  CURRICULUM_LANGUAGE_SELECT_WRAPPER: 'course-edit-curriculum-language-select',
  CURRICULUM_LANGUAGE_OPTION_PREFIX: 'course-edit-curriculum-language-option',
  CURRICULUM_LANGUAGE_COPY_GROUP: 'course-edit-curriculum-copy-group',
  CURRICULUM_LANGUAGE_COPY_BUTTON: 'course-edit-curriculum-copy-button',
  SETTINGS_SECTION: 'course-edit-settings-section',
  SETTINGS_PUBLIC_TOGGLE: 'course-edit-settings-public-toggle',
  SETTINGS_COMMENTS_TOGGLE: 'course-edit-settings-comments-toggle',
  SETTINGS_SAVE_BUTTON: 'course-edit-settings-save-button',
  TAB_BASIC: 'course-edit-tab-basic',
  TAB_CURRICULUM: 'course-edit-tab-curriculum',
  TAB_PRICING: 'course-edit-tab-pricing',
  TAB_RESOURCES: 'course-edit-tab-resources',
  TAB_SETTINGS: 'course-edit-tab-settings',
  TAB_PUBLISH: 'course-edit-tab-publish',
  HEADER_SAVE_BUTTON: 'course-edit-save-button',
  HEADER_RESET_BUTTON: 'course-edit-reset-button',
  HEADER_PREVIEW_BUTTON: 'course-edit-preview-button',
  HEADER_PUBLISH_BUTTON: 'course-edit-publish-button',
  CURRICULUM_SECTION_TITLE_INPUT: 'course-edit-curriculum-section-title-input',
  CURRICULUM_LECTURE_TITLE_INPUT: 'course-edit-curriculum-lecture-title-input',
} as const;

export const COURSE_DETAIL_TEST_IDS = {
  ROOT: 'course-detail-root',
  LANGUAGE_SELECT: 'course-detail-language-select',
  DESCRIPTION_SECTION: 'course-detail-description',
  PRICE_SECTION: 'course-detail-price',
  SECTION_ITEM_PREFIX: 'course-detail-section',
  LECTURE_ITEM_PREFIX: 'course-detail-lecture',
  // Review Section
  VIEW_ALL_REVIEWS_BUTTON: 'view-all-reviews-button',
  REVIEW_SECTION: 'course-detail-review-section',
  REVIEW_ITEM_PREFIX: 'course-detail-review-item',
  REVIEW_EMPTY_STATE: 'course-detail-review-empty-state',
} as const;

// ===== CourseReviewsPage Test IDs =====
export const COURSE_REVIEWS_PAGE_TEST_IDS = {
  ROOT: 'course-reviews-page-root',
  PAGE_TITLE: 'course-reviews-page-title',
  WRITE_REVIEW_BUTTON: 'write-review-button',
  REVIEW_LIST: 'review-list',
  REVIEW_ITEM_PREFIX: 'review-item',
  REVIEW_MODAL: 'course-review-modal',
  REVIEW_MODAL_RATING: 'course-review-modal-rating',
  REVIEW_MODAL_COMMENT: 'course-review-modal-comment',
  REVIEW_MODAL_SUBMIT: 'course-review-modal-submit',
  REVIEW_MODAL_CANCEL: 'course-review-modal-cancel',
  LOADING_SPINNER: 'loading-spinner',
  ERROR_DISPLAY: 'error-display',
  EMPTY_STATE: 'reviews-empty-state',
} as const;

// ===== UserProfilePreview Component Test IDs =====
// 📍 Source: packages/entities/user/src/model/components/UserProfilePreview/UserProfilePreview.tsx
export const USER_PROFILE_PREVIEW_TEST_IDS = {
  // Main container - Line 94
  USER_PROFILE_PREVIEW: 'user-profile-preview',
  
  // Tablet view - Line 99
  TABLET_PROFILE_BUTTON: 'tablet-profile-button',
  
  // Desktop view - Line 150
  DESKTOP_PROFILE_BUTTON: 'desktop-profile-button',
  
  // Dropdown elements - Lines 114, 116, 171
  USER_PROFILE_DROPDOWN: 'user-profile-dropdown',
  DROPDOWN_USER_INFO: 'dropdown-user-info',
  
  // Menu items - Lines 127, 138, 180, 191
  SETTINGS_MENU_ITEM: 'settings-menu-item',
  LOGOUT_MENU_ITEM: 'logout-menu-item',
} as const;

// ===== Reward App Test IDs =====
export const REWARD_APP_TEST_IDS = {
  // Login CTA
  LOGIN_BUTTON: 'reward-login-button',

  // Authenticated state indicators
  USER_GREETING: 'reward-user-greeting',
  USER_EMAIL_BADGE: 'reward-user-email-badge',
} as const;

// ===== Profile Page Test IDs =====
export const PROFILE_PAGE_TEST_IDS = {
  // Main Container
  PROFILE_PAGE_WRAPPER: 'profile-page-wrapper',
  PROFILE_CONTENT_CONTAINER: 'profile-content-container',
  
  // Banner Section
  PROFILE_BANNER_SECTION: 'profile-banner-section',
  PROFILE_BANNER_IMAGE: 'profile-banner-image',
  PROFILE_GO_BACK_BUTTON: 'profile-go-back-button',
  
  // Header Section
  PROFILE_HEADER: 'profile-header',
  PROFILE_AVATAR: 'profile-avatar',
  PROFILE_USERNAME: 'profile-username',
  PROFILE_DISPLAY_NAME: 'profile-display-name',
  PROFILE_BIO: 'profile-bio',
  PROFILE_STATS: 'profile-stats',
  PROFILE_FOLLOWERS_COUNT: 'profile-followers-count',
  PROFILE_FOLLOWING_COUNT: 'profile-following-count',
  PROFILE_POSTS_COUNT: 'profile-posts-count',
  
  // Action Buttons
  PROFILE_EDIT_BUTTON: 'profile-edit-button',
  PROFILE_FOLLOW_BUTTON: 'profile-follow-button',
  PROFILE_MESSAGE_BUTTON: 'profile-message-button',
  PROFILE_SHARE_BUTTON: 'profile-share-button',
  
  // Tabs
  PROFILE_TABS: 'profile-tabs',
  PROFILE_TAB_TWEETS: 'profile-tab-tweets',
  PROFILE_TAB_PROJECTS: 'profile-tab-projects', 
  PROFILE_TAB_COURSES: 'profile-tab-courses',
  PROFILE_TAB_LEARNING: 'profile-tab-learning',
  PROFILE_TAB_TEACHING: 'profile-tab-teaching',
  PROFILE_TAB_ROADMAPS: 'profile-tab-roadmaps',
  PROFILE_COURSES_SUBTAB_IN_PROGRESS: 'profile-courses-subtab-in-progress',
  PROFILE_COURSES_SUBTAB_COMPLETED: 'profile-courses-subtab-completed',
  PROFILE_COURSES_SUBTAB_PUBLISHED: 'profile-courses-subtab-published',
  PROFILE_COURSES_SUBTAB_DRAFTS: 'profile-courses-subtab-drafts',
  
  // Content
  PROFILE_CONTENT: 'profile-content',
  PROFILE_TWEETS_LIST: 'profile-tweets-list',
  PROFILE_PROJECTS_LIST: 'profile-projects-list',
  PROFILE_COURSES_LIST: 'profile-courses-list',
  PROFILE_COURSES_LIST_ITEM: 'profile-courses-list-item',
  PROFILE_ROADMAPS_LIST: 'profile-roadmaps-list',
  
  // Loading States
  PROFILE_LOADING: 'profile-loading',
  PROFILE_TWEETS_LOADING: 'profile-tweets-loading',
  PROFILE_PROJECTS_LOADING: 'profile-projects-loading',
  
  // Load More Buttons
  LOAD_MORE_PROJECTS: 'load-more-projects',
  LOAD_MORE_COURSES: 'load-more-courses', 
  LOAD_MORE_ROADMAPS: 'load-more-roadmaps',
  
  // Empty States
  PROFILE_EMPTY_TWEETS: 'profile-empty-tweets',
  PROFILE_EMPTY_PROJECTS: 'profile-empty-projects',
  PROFILE_EMPTY_COURSES: 'profile-empty-courses',
  PROFILE_EMPTY_ROADMAPS: 'profile-empty-roadmaps',
  
  // Error States  
  PROFILE_ERROR: 'profile-error',
  PROFILE_NOT_FOUND: 'profile-not-found',
} as const;

// ===== Project Upload Page Test IDs =====
export const PROJECT_UPLOAD_TEST_IDS = {
  PAGE_TITLE: 'project-upload-title',
  PAGE_FORM: 'project-upload-form',
  INFO_SECTION: 'project-upload-info-section',
  FILE_SECTION: 'project-upload-file-section',
  MEDIA_SECTION: 'project-upload-media-section',
  TITLE_INPUT: 'project-upload-title-input',
  DESCRIPTION_INPUT: 'project-upload-description-input',
  TAG_INPUT: 'project-upload-tag-input',
  ADD_TAG_BUTTON: 'project-upload-add-tag-button',
  FOLDER_UPLOAD_BUTTON: 'project-upload-folder-upload-button',
  ZIP_UPLOAD_BUTTON: 'project-upload-zip-upload-button',
  ZIP_SUMMARY: 'project-upload-zip-summary',
  FILE_TREE: 'project-upload-file-tree',
  VALIDATION_MESSAGE: 'project-upload-validation-message',
  MEDIA_ADD_BUTTON: 'project-upload-media-add-button',
  MEDIA_ITEM: 'project-upload-media-item',
  CANCEL_BUTTON: 'project-upload-cancel-button',
  SUBMIT_BUTTON: 'project-upload-submit-button',
  SAVE_TOAST: 'project-upload-save-toast',
} as const;

// ===== Profile Edit Page Test IDs =====
export const PROFILE_EDIT_TEST_IDS = {
  PROFILE_EDIT_FORM: 'profile-edit-form',
  PROFILE_EDIT_TITLE: 'profile-edit-title',
  PROFILE_EDIT_DISPLAY_NAME: 'profile-edit-display-name',
  PROFILE_EDIT_USERNAME: 'profile-edit-username',
  PROFILE_EDIT_BIO: 'profile-edit-bio',
  PROFILE_EDIT_LOCATION: 'profile-edit-location',
  PROFILE_EDIT_WEBSITE: 'profile-edit-website',
  PROFILE_EDIT_SUBMIT: 'profile-edit-submit',
  PROFILE_EDIT_CANCEL: 'profile-edit-cancel',
  // Image upload inputs/triggers
  PROFILE_EDIT_AVATAR_INPUT: 'profile-edit-avatar-input',
  PROFILE_EDIT_COVER_INPUT: 'profile-edit-cover-input',
  PROFILE_EDIT_AVATAR_TRIGGER: 'profile-edit-avatar-trigger',
  PROFILE_EDIT_COVER_TRIGGER: 'profile-edit-cover-trigger',
} as const;

// ===== Chat Test IDs =====
export const CHAT_TEST_IDS = {
  ACTION_MENU_TRIGGER: 'chat-action-menu-trigger',
  SIDEBAR_TOGGLE_BUTTON: 'chat-sidebar-toggle-button',
  AI_TO_PEOPLE_SWITCH_BUTTON: 'chat-switch-to-messages-button',
  LIST_NEW_CHAT_BUTTON: 'chat-new-chat-button',
  LIST_SEARCH_INPUT: 'chat-list-search-input',
  LIST_CONTAINER: 'chat-list',
  LIST_ITEMS_WRAPPER: 'chat-list-items',
  CREATE_CHAT_SEARCH_INPUT: 'chat-create-search-input',
  HISTORY_VIEW_BUTTON: 'chat-history-view-button',
  HISTORY_ITEM: 'chat-history-item',

  // 1:1 Chat Flow (T014)
  OPEN_BUTTON: 'chat-open-button',
  USER_SEARCH: 'chat-user-search',
  USER_RESULT_PREFIX: 'chat-user-result',
  MESSAGE_INPUT: 'chat-message-input',
  SEND_BUTTON: 'chat-send-button',
  MESSAGE_PREFIX: 'chat-message',
  MESSAGE_STATUS_SUFFIX: '-status',
  NOTIFICATION: 'chat-notification',
  TYPING_INDICATOR: 'chat-typing-indicator',
  ROOM_ITEM_PREFIX: 'chat-room-item',
  ROOM_NAME_PREFIX: 'chat-room-name',
  ROOM_UNREAD_BADGE_PREFIX: 'chat-room-unread',
  ROOM_MORE_BUTTON_PREFIX: 'chat-room-more-button',
  ROOM_CONTEXT_MENU: 'chat-room-context-menu',
  ROOM_MENU_LEAVE_BUTTON: 'chat-room-menu-leave-button',
  BACK_TO_LIST_BUTTON: 'chat-back-to-list-button',

  // Group Chat (T015)
  CREATE_GROUP: 'chat-create-group',
  GROUP_NAME_INPUT: 'group-name-input',
  GROUP_ADD_MEMBER: 'group-add-member',
  GROUP_MEMBER_RESULT_PREFIX: 'group-member-result',
  GROUP_CREATE_CONFIRM: 'group-create-confirm',
  MESSAGE_READ_STATUS_SUFFIX: '-read-status',

  // File Upload (T016)
  FILE_UPLOAD_BUTTON: 'chat-file-upload-button',
  FILE_UPLOAD_INPUT: 'chat-file-upload-input',
  FILE_PREVIEW: 'chat-file-preview',
  FILE_SIZE: 'chat-file-size',
  FILE_ERROR: 'chat-file-error',
  MESSAGE_FILE_SUFFIX: '-file',
  MESSAGE_FILE_NAME_SUFFIX: '-file-name',
  MESSAGE_DOWNLOAD_SUFFIX: '-download',

  // Message Search (T017)
  SEARCH_BUTTON: 'chat-search-button',
  SEARCH_SCOPE_TOGGLE: 'search-scope-toggle',
  SEARCH_INPUT: 'search-input',
  SEARCH_RESULT_PREFIX: 'search-result',
  SEARCH_RESULT_COUNT: 'search-result-count',
  SEARCH_RESULTS: 'search-results',
  SEARCH_RESULT_ROOM_NAME_SUFFIX: '-room-name',
  SEARCH_RESULT_CONTENT_SUFFIX: '-content',
  SEARCH_NO_RESULTS: 'search-no-results',
} as const;

// ===== Notification System Test IDs =====
export const NOTIFICATION_TEST_IDS = {
  // Main Container
  NOTIFICATION_PAGE_WRAPPER: 'notification-page-wrapper',
  NOTIFICATION_LIST_CONTAINER: 'notification-list-container',

  // Header & Actions
  NOTIFICATION_HEADER: 'notification-header',
  NOTIFICATION_MARK_ALL_READ_BUTTON: 'notification-mark-all-read',
  NOTIFICATION_SETTINGS_BUTTON: 'notification-settings-button',
  NOTIFICATION_FILTER_TABS: 'notification-filter-tabs',
  NOTIFICATION_FILTER_ALL: 'notification-filter-all',
  NOTIFICATION_FILTER_UNREAD: 'notification-filter-unread',

  // Notification Items
  NOTIFICATION_ITEM: 'notification-item',
  NOTIFICATION_ITEM_UNREAD: 'notification-item-unread',
  NOTIFICATION_ITEM_READ: 'notification-item-read',
  NOTIFICATION_AVATAR: 'notification-avatar',
  NOTIFICATION_CONTENT: 'notification-content',
  NOTIFICATION_MESSAGE: 'notification-message',
  NOTIFICATION_TIME: 'notification-time',
  NOTIFICATION_SOURCE_USER: 'notification-source-user',
  NOTIFICATION_ICON: 'notification-icon',

  // Notification Types
  NOTIFICATION_TYPE_LIKE: 'notification-type-like',
  NOTIFICATION_TYPE_REPLY: 'notification-type-reply',
  NOTIFICATION_TYPE_RETWEET: 'notification-type-retweet',
  NOTIFICATION_TYPE_FOLLOW: 'notification-type-follow',
  NOTIFICATION_TYPE_MENTION: 'notification-type-mention',

  // Unread Count Badge
  NOTIFICATION_BADGE: 'notification-badge',
  NOTIFICATION_UNREAD_COUNT: 'notification-unread-count',

  // Empty State
  NOTIFICATION_EMPTY_STATE: 'notification-empty-state',
  NOTIFICATION_EMPTY_MESSAGE: 'notification-empty-message',

  // Loading State
  NOTIFICATION_LOADING: 'notification-loading',
  NOTIFICATION_LOADING_SKELETON: 'notification-loading-skeleton',

  // Error State
  NOTIFICATION_ERROR: 'notification-error',
  NOTIFICATION_ERROR_MESSAGE: 'notification-error-message',
  NOTIFICATION_RETRY_BUTTON: 'notification-retry-button',

  // Pagination
  NOTIFICATION_LOAD_MORE_BUTTON: 'notification-load-more',
  NOTIFICATION_PAGINATION_INFO: 'notification-pagination-info',

  // Settings Modal
  NOTIFICATION_SETTINGS_MODAL: 'notification-settings-modal',
  NOTIFICATION_SETTINGS_CONTAINER: 'notification-settings-container',
  NOTIFICATION_SETTINGS_LOADING: 'notification-settings-loading',
  NOTIFICATION_SETTINGS_ERROR: 'notification-settings-error',
  NOTIFICATION_SETTINGS_EMAIL: 'notification-settings-email',
  NOTIFICATION_SETTINGS_SMS: 'notification-settings-sms',
  NOTIFICATION_SETTINGS_PUSH: 'notification-settings-push',
  NOTIFICATION_SETTINGS_IN_APP: 'notification-settings-in-app',
  NOTIFICATION_SETTINGS_QUIET_START: 'notification-settings-quiet-start',
  NOTIFICATION_SETTINGS_QUIET_END: 'notification-settings-quiet-end',
  NOTIFICATION_SETTINGS_TYPE_PREFIX: 'notification-settings-type-',
  NOTIFICATION_SETTINGS_SAVE_BUTTON: 'notification-settings-save',
  NOTIFICATION_SETTINGS_CANCEL_BUTTON: 'notification-settings-cancel',
  // Legacy aliases (for backward compatibility)
  SETTINGS_MODAL: 'notification-settings-modal',
  SETTINGS_EMAIL_TOGGLE: 'notification-settings-email-toggle',
  SETTINGS_PUSH_TOGGLE: 'notification-settings-push-toggle',
  SETTINGS_INAPP_TOGGLE: 'notification-settings-inapp-toggle',
  SETTINGS_QUIET_HOURS_START: 'notification-settings-quiet-start',
  SETTINGS_QUIET_HOURS_END: 'notification-settings-quiet-end',
  SETTINGS_TYPE_LIKE_TOGGLE: 'notification-settings-type-like',
  SETTINGS_TYPE_REPLY_TOGGLE: 'notification-settings-type-reply',
  SETTINGS_TYPE_RETWEET_TOGGLE: 'notification-settings-type-retweet',
  SETTINGS_TYPE_FOLLOW_TOGGLE: 'notification-settings-type-follow',
  SETTINGS_TYPE_MENTION_TOGGLE: 'notification-settings-type-mention',
  SETTINGS_SAVE_BUTTON: 'notification-settings-save',
  SETTINGS_CANCEL_BUTTON: 'notification-settings-cancel',

  // Real-time Connection
  SSE_CONNECTION_STATUS: 'notification-sse-status',
  SSE_CONNECTION_ERROR: 'notification-sse-error',
  SSE_RECONNECTING: 'notification-sse-reconnecting',
} as const;

// ===== User Server Test IDs =====
export const USER_SERVER_TEST_IDS = {
  // User Profile
  USER_PROFILE_CONTAINER: 'user-profile-container',
  USER_PROFILE_HEADER: 'user-profile-header',
  USER_ID_DISPLAY: 'user-id-display',
  USER_NAME_DISPLAY: 'user-name-display',
  USER_EMAIL_DISPLAY: 'user-email-display',
  USER_ROLE_DISPLAY: 'user-role-display',
  USER_STATUS_DISPLAY: 'user-status-display',
  
  // User Actions
  USER_EDIT_BUTTON: 'user-edit-button',
  USER_DELETE_BUTTON: 'user-delete-button',
  USER_ACTIVATE_BUTTON: 'user-activate-button',
  USER_DEACTIVATE_BUTTON: 'user-deactivate-button',
  
  // User List
  USER_LIST_CONTAINER: 'user-list-container',
  USER_LIST_ITEM: 'user-list-item',
  USER_LIST_SEARCH: 'user-list-search',
  USER_LIST_FILTER: 'user-list-filter',
  
  // API Response
  API_RESPONSE_DATA: 'api-response-data',
  API_ERROR_MESSAGE: 'api-error-message',
  SYNC_STATUS_INDICATOR: 'sync-status-indicator',
} as const;

// ===== Project Management Test IDs =====
export const PROJECT_TEST_IDS = {
  // Project List Page
  PROJECT_LIST: 'project-list',
  PROJECT_CARD: 'project-card',
  CREATE_PROJECT_BUTTON: 'create-project-button',
  FILTER_CATEGORY: 'filter-category',
  FILTER_STATUS: 'filter-status',
  SEARCH_INPUT: 'search-input',

  // Project Detail Page
  PROJECT_TITLE: 'project-title',
  PROJECT_DESCRIPTION: 'project-description',
  PROJECT_CONTENT: 'project-content',
  EDIT_PROJECT_BUTTON: 'edit-project-button',
  DELETE_PROJECT_BUTTON: 'delete-project-button',
  CONFIRM_DELETE_BUTTON: 'confirm-delete-button',
  LIKE_BUTTON: 'like-button',
  LIKE_COUNT: 'like-count',
  STAR_BUTTON: 'star-button',
  SHARE_BUTTON: 'share-button',

  // Collaborators Section
  COLLABORATORS_SECTION: 'collaborators-section',
  COLLABORATOR_ITEM: 'collaborator-item',
  ADD_COLLABORATOR_BUTTON: 'add-collaborator-button',
  COLLABORATOR_USERNAME_INPUT: 'collaborator-username-input',
  COLLABORATOR_ROLE_SELECT: 'collaborator-role-select',
  INVITE_COLLABORATOR_SUBMIT: 'invite-collaborator-submit',
  REMOVE_COLLABORATOR_BUTTON: 'remove-collaborator-button',
  CONFIRM_REMOVE_BUTTON: 'confirm-remove-button',

  // Milestones Section
  MILESTONES_SECTION: 'milestones-section',
  MILESTONE_ITEM: 'milestone-item',
  ADD_MILESTONE_BUTTON: 'add-milestone-button',
  MILESTONE_TITLE_INPUT: 'milestone-title-input',
  MILESTONE_DESCRIPTION_INPUT: 'milestone-description-input',
  MILESTONE_DUE_DATE_INPUT: 'milestone-due-date-input',
  MILESTONE_SUBMIT_BUTTON: 'milestone-submit-button',
  MILESTONE_COMPLETE_CHECKBOX: 'milestone-complete-checkbox',
  PROGRESS_BAR: 'progress-bar',

  // Media Section
  MEDIA_SECTION: 'media-section',
  MEDIA_ITEM: 'media-item',
  UPLOAD_MEDIA_BUTTON: 'upload-media-button',
  FILE_INPUT: 'file-input',
  DELETE_MEDIA_BUTTON: 'delete-media-button',
  UPLOAD_PROGRESS: 'upload-progress',

  // Project Form
  FORM_TITLE_INPUT: 'form-title-input',
  FORM_DESCRIPTION_INPUT: 'form-description-input',
  FORM_CONTENT_INPUT: 'form-content-input',
  FORM_CATEGORY_SELECT: 'form-category-select',
  FORM_STATUS_SELECT: 'form-status-select',
  FORM_TAGS_INPUT: 'form-tags-input',
  FORM_TECH_STACK_INPUT: 'form-tech-stack-input',
  FORM_REPOSITORY_URL_INPUT: 'form-repository-url-input',
  FORM_WEBSITE_URL_INPUT: 'form-website-url-input',
  FORM_SUBMIT_BUTTON: 'form-submit-button',
  FORM_CANCEL_BUTTON: 'form-cancel-button',
} as const;

// ===== Common/Shared Test IDs =====
export const COMMON_TEST_IDS = {
  // Loading States
  LOADING_SPINNER: 'loading-spinner',
  LOADING_SKELETON: 'loading-skeleton',

  // Modals
  MODAL: 'modal',
  MODAL_CLOSE: 'modal-close',
  MODAL_CONFIRM: 'modal-confirm',
  MODAL_CANCEL: 'modal-cancel',

  // Alerts/Toasts
  ALERT: 'alert',
  TOAST: 'toast',
  NOTIFICATION: 'notification',

  // Forms
  FORM_ERROR: 'form-error',
  FORM_SUCCESS: 'form-success',
  SUBMIT_BUTTON: 'submit-button',
  CANCEL_BUTTON: 'cancel-button',
} as const;

export const MAIL_ACCOUNT_TEST_IDS = {
  REMOTE_WIDGET: 'mail-remote-widget',
  REMOTE_ACCOUNT_SELECT: 'mail-remote-account-select',
  REMOTE_FOLDER_SELECT: 'mail-remote-folder-select',
  REMOTE_SEARCH_INPUT: 'mail-remote-search-input',
  REMOTE_SEARCH_SUBMIT: 'mail-remote-search-submit',
  REMOTE_COMPOSE: 'mail-remote-compose',
  REMOTE_DETAIL: 'mail-remote-detail',
  REMOTE_BODY: 'mail-remote-body',
  REMOTE_SEND: 'mail-remote-send',
  SYNC_NOTICE: 'mail-account-sync-notice',
  SYNC_BUTTON: 'mail-account-sync-button',
  UPLOAD_BUTTON: 'mail-account-upload-button',
  ACCOUNT_STATE: 'mail-account-state',
} as const;

// ===== Helper Functions =====

/**
 * Test ID를 data-testid 속성 선택자로 변환
 * @param testId - 테스트 ID 상수
 * @returns data-testid 선택자 문자열
 */
export function toTestIdSelector(testId: string): string {
  return `[data-testid="${testId}"]`;
}

/**
 * Playwright getByTestId 헬퍼와 함께 사용
 * @param testId - 테스트 ID 상수
 * @returns 테스트 ID 문자열
 */
export function getTestId(testId: string): string {
  return testId;
}

// Type exports for type safety
export type LandingPageTestId = typeof LANDING_PAGE_TEST_IDS[keyof typeof LANDING_PAGE_TEST_IDS];
export type AuthServerTestId = typeof AUTH_SERVER_TEST_IDS[keyof typeof AUTH_SERVER_TEST_IDS];
export type MainLayoutTestId = typeof MAIN_LAYOUT_TEST_IDS[keyof typeof MAIN_LAYOUT_TEST_IDS];
export type FeedPageTestId = typeof FEED_PAGE_TEST_IDS[keyof typeof FEED_PAGE_TEST_IDS];
export type TweetComposerTestId = typeof TWEET_COMPOSER_TEST_IDS[keyof typeof TWEET_COMPOSER_TEST_IDS];
export type SettingsPageTestId = typeof SETTINGS_PAGE_TEST_IDS[keyof typeof SETTINGS_PAGE_TEST_IDS];
export type CourseEditPageTestId = typeof COURSE_EDIT_TEST_IDS[keyof typeof COURSE_EDIT_TEST_IDS];
export type CourseDetailTestId = typeof COURSE_DETAIL_TEST_IDS[keyof typeof COURSE_DETAIL_TEST_IDS];
export type CourseReviewsPageTestId = typeof COURSE_REVIEWS_PAGE_TEST_IDS[keyof typeof COURSE_REVIEWS_PAGE_TEST_IDS];
export type ProfilePageTestId = typeof PROFILE_PAGE_TEST_IDS[keyof typeof PROFILE_PAGE_TEST_IDS];
export type ProfileEditPageTestId = typeof PROFILE_EDIT_TEST_IDS[keyof typeof PROFILE_EDIT_TEST_IDS];
export type ChatTestId = typeof CHAT_TEST_IDS[keyof typeof CHAT_TEST_IDS];
export type NotificationTestId = typeof NOTIFICATION_TEST_IDS[keyof typeof NOTIFICATION_TEST_IDS];
export type UserServerTestId = typeof USER_SERVER_TEST_IDS[keyof typeof USER_SERVER_TEST_IDS];
export type ProjectTestId = typeof PROJECT_TEST_IDS[keyof typeof PROJECT_TEST_IDS];
export type CommonTestId = typeof COMMON_TEST_IDS[keyof typeof COMMON_TEST_IDS];

// Export all test IDs in one object for convenience
export const ALL_TEST_IDS = {
  landing: LANDING_PAGE_TEST_IDS,
  auth: AUTH_SERVER_TEST_IDS,
  layout: MAIN_LAYOUT_TEST_IDS,
  feed: FEED_PAGE_TEST_IDS,
  tweetComposer: TWEET_COMPOSER_TEST_IDS,
  settings: SETTINGS_PAGE_TEST_IDS,
  courseEdit: COURSE_EDIT_TEST_IDS,
  courseDetail: COURSE_DETAIL_TEST_IDS,
  courseReviews: COURSE_REVIEWS_PAGE_TEST_IDS,
  profile: PROFILE_PAGE_TEST_IDS,
  profileEdit: PROFILE_EDIT_TEST_IDS,
  chat: CHAT_TEST_IDS,
  notification: NOTIFICATION_TEST_IDS,
  userServer: USER_SERVER_TEST_IDS,
  project: PROJECT_TEST_IDS,
  common: COMMON_TEST_IDS,
  mailAccount: MAIL_ACCOUNT_TEST_IDS,
} as const;
export const PALJA_CATALOG_ADMIN_TEST_IDS = {
  panel: 'palja-catalog-admin',
  refresh: 'palja-catalog-refresh',
  product: (id: string) => `palja-catalog-product-${id}`,
  title: (id: string) => `palja-catalog-title-${id}`,
  description: (id: string) => `palja-catalog-description-${id}`,
  active: (id: string) => `palja-catalog-active-${id}`,
  sortOrder: (id: string) => `palja-catalog-sort-${id}`,
  save: (id: string) => `palja-catalog-save-${id}`,
  coverFile: (id: string) => `palja-catalog-cover-file-${id}`,
  upload: (id: string) => `palja-catalog-cover-upload-${id}`,
} as const;

export const DAON_USAGE_TEST_IDS = {
  page: 'daon-usage-credits',
  refresh: 'daon-usage-refresh',
  policy: 'daon-usage-policy',
  userLookup: 'daon-credit-user-lookup',
  adjustment: 'daon-credit-adjustment',
  preview: 'daon-credit-preview',
  apply: 'daon-credit-apply',
  history: 'daon-credit-history',
} as const;
